import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import ScrollReveal from '@/components/base/ScrollReveal';

const FORM_URL = 'https://readdy.ai/api/form/d8t6c6sal24muhn2r410';
const WhatsAppNumber = '919686543847';
const InstagramHandle = '@joviaa.jewellery';
const EmailAddress = 'hello@joviaa.com';

export default function ContactPage() {
  const { toast, showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const honeypot = (e.target as HTMLFormElement).querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      showToast('Thanks for reaching out! We\'ll get back to you soon.');
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      showToast('Please fill in your name, email, and message.');
      return;
    }
    if (formData.message.length > 500) {
      showToast('Please keep your message under 500 characters.');
      return;
    }
    setLoading(true);
    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }).toString(),
      });
    } catch {
      // Form submitted best-effort
    }
    setLoading(false);
    showToast('Message sent! We\'ll get back to you within a few hours.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <main className="min-h-screen bg-background-50">
      {/* Toast */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm shadow-lg transition-all duration-300 pointer-events-none ${
          toast.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        {toast.message}
      </div>

      {/* Hero Section */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20soft%20abstract%20golden%20and%20cream%20gradient%20waves%20with%20subtle%20sand%20texture%2C%20elegant%20minimal%20background%20with%20gentle%20metallic%20light%20reflections%2C%20peaceful%20serene%20aesthetic%2C%20no%20people%2C%20sophisticated%20ambient%20warm%20glow&width=1600&height=550&seq=contact-hero-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center opacity-25"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24 flex flex-col items-center text-center">
          <p className="text-accent-400 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-4">
            Let&apos;s Talk
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 max-w-3xl leading-tight">
            Get in Touch
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-background-50/80 font-label max-w-2xl leading-relaxed">
            Questions, feedback, or just want to say hi? We&apos;d love to hear from you. Pick your preferred way to reach us below.
          </p>
        </div>
      </section>

      {/* Contact Channels */}
      <section className="max-w-5xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* WhatsApp Card */}
          <ScrollReveal delay={0}>
            <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 text-center group hover:border-accent-300 transition-colors">
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-whatsapp-line text-2xl text-accent-600"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary-500 mb-2">WhatsApp</h3>
              <p className="text-sm text-foreground-500 font-label mb-5 leading-relaxed">
                Fastest way to reach us. Chat with a real person, get order updates, or ask anything.
              </p>
              <a
                href={`https://wa.me/${WhatsAppNumber}?text=${encodeURIComponent('Hi Joviaa! I have a question.')}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-primary-800 rounded-full font-heading font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-fill"></i>
                </div>
                Start Chat
              </a>
            </div>
          </ScrollReveal>

          {/* Instagram Card */}
          <ScrollReveal delay={120}>
            <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 text-center group hover:border-accent-300 transition-colors">
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-instagram-line text-2xl text-accent-600"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary-500 mb-2">Instagram</h3>
              <p className="text-sm text-foreground-500 font-label mb-5 leading-relaxed">
                Follow us for new drops, styling inspiration, and a peek behind the scenes.
              </p>
              <a
                href={`https://instagram.com/${InstagramHandle.replace('@', '')}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-primary-800 rounded-full font-heading font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-instagram-fill"></i>
                </div>
                {InstagramHandle}
              </a>
            </div>
          </ScrollReveal>

          {/* Email Card */}
          <ScrollReveal delay={240}>
            <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8 text-center group hover:border-accent-300 transition-colors">
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-mail-line text-2xl text-accent-600"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary-500 mb-2">Email</h3>
              <p className="text-sm text-foreground-500 font-label mb-5 leading-relaxed">
                Prefer email? Drop us a line and we&apos;ll respond within 24 hours.
              </p>
              <a
                href={`mailto:${EmailAddress}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-primary-800 rounded-full font-heading font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-fill"></i>
                </div>
                {EmailAddress}
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-accent-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-14">
              <p className="text-accent-600 text-xs md:text-sm font-label font-semibold uppercase tracking-[0.2em] mb-3">
                Drop Us a Message
              </p>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-primary-500">
                Send Us a Message
              </h2>
              <p className="mt-3 text-foreground-600 font-label text-sm md:text-base max-w-lg mx-auto">
                Fill out the form below and we&apos;ll get back to you within a few hours.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-10">
              <form
                onSubmit={handleSubmit}
                data-readdy-form="true"
                className="flex flex-col gap-5"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="contact-alt-field"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-name" className="text-sm font-label font-semibold text-foreground-700">
                      Name <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="px-4 py-3 text-sm font-label border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-email" className="text-sm font-label font-semibold text-foreground-700">
                      Email <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="px-4 py-3 text-sm font-label border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-subject" className="text-sm font-label font-semibold text-foreground-700">
                    Subject
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What's this about?"
                    className="px-4 py-3 text-sm font-label border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-message" className="text-sm font-label font-semibold text-foreground-700">
                    Message <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us what's on your mind..."
                    rows={5}
                    maxLength={500}
                    required
                    className="px-4 py-3 text-sm font-label border border-background-300 rounded-lg bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                  />
                  <span className="text-xs text-foreground-400 font-label text-right">
                    {formData.message.length} / 500
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto sm:self-start px-8 py-3.5 bg-primary-500 text-background-50 rounded-lg font-heading font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></span>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Banner */}
      <ScrollReveal>
        <section className="bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                Prefer to browse first?
              </h2>
              <p className="mt-2 text-sm md:text-base text-background-50/70 font-label max-w-lg">
                Explore our Life-Proof Gold collections — designed to keep up with your every day.
              </p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 whitespace-nowrap flex-wrap">
              <Link
                to="/shop"
                className="px-7 py-3 bg-background-50 text-primary-500 rounded-lg font-heading font-semibold text-sm hover:bg-background-50/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                Shop All
              </Link>
              <Link
                to="/faq"
                className="px-7 py-3 border-2 border-background-50/30 text-background-50 rounded-lg font-heading font-semibold text-sm hover:border-background-50/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                Read FAQ
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </main>
  );
}